import { _ } from "../../polyglot/polyglot.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import dialog from "../dialog.js";
import regionWidget from "../regionWidget.js";
import SearchFieldWidget from "../SearchFieldWidget/SearchFieldWidget.js";

/**
 * `DimensionObjectPicker` is a dialog used to create a custom selection of
 * dimension objects (leaves of a dimension tree) during the import last
 * step.
 *
 * @param {Array} spec.dimensionObjects - Array with all dimension objects.
 * @param {Array} spec.selection - Array with id's of the current selection.
 */
export default class DimensionObjectPicker extends Widget {
	constructor({ dimensionObjects, selection }) {
		super(...arguments);

		this._createEvents("onChange");
		this._dimensionObjects = dimensionObjects;
		this._selection = new Set(selection);
		this._searchString = "";

		this.onChange.register(() => {
			this._total.update();
			this._selectAllObjects.update();
		});

		this._dialog = dialog({
			header: _("Select objects to import"),
			body: this._renderDialogBodyOn.bind(this),
			footer: this._renderDialogFooterOn.bind(this),
		});

		this._searchField = new SearchFieldWidget({
			placeholder: _("Filter on name"),
			inputId: `searchfield-${this.getId()}`,
		});

		this._searchField.applySearchOn(this);

		this._list = regionWidget({
			content: (html) => {
				html.ul(
					this._dimensionObjects.map(this._objectRenderer.bind(this)),
				);
			},
		});

		this._total = regionWidget({
			content: (html) => {
				html.div(
					{ klass: "pull-right" },
					_("{selected}/{total}", {
						selected: this._selection.size,
						total: this._dimensionObjects.length,
					}),
				);
			},
		});

		this._selectAllObjects = regionWidget({
			content: (html) => {
				let label;
				let action;

				if (this._selection.size === this._dimensionObjects.length) {
					label = _("Deselect All");
					action = this._deselectAll.bind(this);
				} else {
					label = _("Select All");
					action = this._selectAll.bind(this);
				}

				html.a({ klass: "pull-left", click: action }, label);
			},
		});
	}

	applySearchFrom(searchWidget) {
		searchWidget.applySearchOnList(this);
	}

	updateSearch(string) {
		this._searchString = string.toLowerCase();
		this._list.update();
	}

	getSelected() {
		return [...this._selection];
	}

	show() {
		this._dialog.show();
	}

	hide() {
		this._dialog.hide();
	}

	renderContentOn(html) {
		html.render(this._dialog);
	}

	_renderDialogBodyOn(html) {
		html.div(
			{ klass: "dimension-object-dialog" },
			html.div(
				{ klass: "clearfix" },
				html.span({ klass: "fiscal-year-object-label" }, _("Search:")),
				this._searchField,
			),
			html.div({ klass: "fiscal-year-object-picker" }, this._list),
			html.div(
				{ klass: "dimension-object-dialog-info-line" },
				this._selectAllObjects,
				this._total,
			),
		);
	}

	_objectRenderer(object) {
		if (!this._shouldDisplayObject(object)) {
			return "";
		}
		return (html) => {
			let input;
			const checked = this._selection.has(object.id);

			html.li(
				{ klass: "dimension-object" },
				html
					.div(
						{ klass: "form check-group" },
						html.label(
							(html) => {
								input = html.input({ type: "checkbox" });
							},
							html.span({ klass: "label-strong" }, object.name),
						),
					)
					.on("change", () => {
						this._selection.has(object.id)
							? this._selection.delete(object.id)
							: this._selection.add(object.id);

						this.onChange.trigger();
					}),
				this._objectInfoRenderer(object),
			);

			if (checked) {
				input.attr({
					checked: "checked",
				});
			}
		};
	}

	_objectInfoRenderer(object) {
		return (html) =>
			html.div(
				{ klass: "dimension-object-info" },
				html.div(
					{ klass: "dimension-object-id" },
					_("Identifier: {id}", { id: object.id }),
				),
				html.div(
					{ klass: "dimension-object-transactions" },
					_("Number of transactions: {total}", {
						total: object.referenceCount,
					}),
				),
			);
	}

	_renderDialogFooterOn(html) {
		html.button(
			{
				klass: "btn btn-primary",
				click: this.hide.bind(this),
			},
			_("Ok"),
		);
	}

	_shouldDisplayObject(object) {
		return object.name.toLowerCase().indexOf(this._searchString) !== -1;
	}

	_selectAll() {
		this._dimensionObjects.forEach((object) => {
			this._selection.add(object.id);
		});

		this._list.update();
		this.onChange.trigger();
	}

	_deselectAll() {
		this._selection.clear();
		this._list.update();
		this.onChange.trigger();
	}
}
